<script lang="ts">
  import { navigate } from 'svelte-routing';
  import { slide } from 'svelte/transition';
  let showAlgorithm = false;
</script>

<svelte:head>
  <title>About - outlive.it</title>
</svelte:head>

<section id="main" class="main">
  <header>
    <h2>About</h2>
  </header>

  <p>
    Outlive is a game that explores slow experiences, power
    and data. You play by living. It is a love song to open data sets and Wikipedia.
  </p>

  <p>
    There are an ad hoc and a long experience of the game. In the ad hoc game, you can
    check your rating at any time at outlive.it. In the long and slow
    experience, you can order a notification to inform you when your rank changes.
    It is the slow experience we are most interested in: will it feel
    different as time goes by? The game is also an anti-version for all the
    digital experiences trying to hog most of our attention. You can help by
    <a href="/feedback" on:click|preventDefault={() => navigate('/feedback')}>anonymously answering
    a few questions</a>.
  </p>

  <h4><a id="why-popes"></a>Why popes?</h4>
  <p>
    We chose papacy because it is the only occupation that has a publicly available
    data set stretching back over 2000 years. Popes also have the best outfits. We are
    also considering adding other rulers and historical figures or maybe even
    fictional characters. The idea for the game came when reading a biography of
    Madame de Pompadour, an accomplished woman of her time, who died at the age of 42.
  </p>

  <h4><a id="disrespectful"></a>Is this meant to be disrespectful?</h4>
  <p>
    No. The game stems from an interest in history and occasional experiences that
    could bridge our present days to past events. It is also an investigation of
    power and the range of feelings outliving figures of authority may evoke.
  </p>
  <p>
    The currently living popes are excluded from being game bosses. We also exercised
    utmost restraint to not include cheap jokes like <span class="eldenring">GREAT ENEMY FELLED</span> in the game.
  </p>

  <h4><a id="bad-taste"></a>Isn't competing with age at death in bad taste? Isn't old age for
      privileged people only?</h4>
  <p>
    Maybe. No. Yes. But age is also something all humans have in common, something
    that ties us to history and the lives of others. Comparison is not really
    important, and in a lot of ways it can be very harmful, but it could provide
    perspective. So we thought, why not?
    However, we are aware that the game is suggesting that living long is a norm to
    pursue and this might be an inappropriate message. We suggest that everyone should
    try to live full lives when they can with as small a carbon footprint as possible.
  </p>

  <h4><a id="date-uncertainty"></a>How do we deal with Popes with uncertain birth and death dates?</h4>
  <p>
    The statisticians collecting data in the early years of the papacy were not overly exact.
    They probably had better things to do, so some pope birth/death dates are
    vague - to say the least. We have applied following treatment to include
    them in the game:
  </p>

  <p>
    <b>1. Vague years</b><br />
    We have calculated a <a target="_blank" href="https://en.wikipedia.org/wiki/Harmonic_mean" rel="noopener noreferrer">Harmonic mean</a> because it sounds
    appropriate and provides the most inclusive playing experience. (It is the least of the Pythagorean means.)
    E.g.
    <span class="example">
      <span class="pope-info"><span class="pope-tag">Pope #1</span> St Peter</span> died 62-67 years old. The Harmonic mean is 64.4 so
      you outlive St Peter in the age of 64 years and 5 months.
    </span>
  </p>

  <p>
    <b>2. Arguable months</b><br />
    Exact days are just luck in the first millennia. We round up the months to
    beginning or end of month. For the popes with only year of birth and death, we
    apply a cutting edge <a href="#date-uncertainty" on:click|preventDefault={() => showAlgorithm = !showAlgorithm}>eschato-numerological algorithm</a> to decide on the month.
    In terms of accuracy this is questionable, but it is made in the interest of
    game design, as it distributes the ages more evenly and reduces pope clusters on
    the time axis.
  </p>

  {#if showAlgorithm}
  <pre transition:slide={{ duration: 220 }}><code
><span class="kw">const</span> <span class="fn">𝖆𝖊𝖙𝖆𝖘</span> = (𝖓𝖔𝖒𝖊𝖓<span class="ty">: string</span>)<span class="ty">: number</span> <span class="kw">=&gt;</span>
  Math.<span class="fn">abs</span>(
    [...𝖓𝖔𝖒𝖊𝖓]
      .<span class="fn">map</span>(𝖘𝖎𝖌𝖎𝖑 <span class="kw">=&gt;</span> 𝖘𝖎𝖌𝖎𝖑.<span class="fn">charCodeAt</span>(<span class="nm">0</span>))
      .<span class="fn">reduce</span>((𝖈𝖔𝖓𝖌𝖊𝖗𝖎𝖊𝖘, 𝖌𝖑𝖞𝖕𝖍) <span class="kw">=&gt;</span> Math.<span class="fn">imul</span>(𝖈𝖔𝖓𝖌𝖊𝖗𝖎𝖊𝖘, <span class="nm">31</span>) + 𝖌𝖑𝖞𝖕𝖍 | <span class="nm">0</span>, <span class="nm">0</span>)
  ) % <span class="nm">12</span>;</code></pre>
  {/if}

  <p>
    <b>3. Unknown dates</b><br />
    Additional rules we added for popes of unknown ages:
  </p>

  <div class="nested">
    <h5>Rules for Popes</h5>
    <p>
      (Age of previous pope by age + Age of next pope by age) / 2. In case of multiple in row, all get same age.
    </p>

    <h5>Rules for Antipopes</h5>
    <p>
      Age of opposing pope -2 years (as antipapacy is probably more dangerous). For example:

      <span class="example">
        <span class="pope-info"><span class="antipope-tag">Antipope #1</span> Natalius</span>
        gets
        <span class="pope-info"><span class="pope-tag">Pope #15</span> St Zephyrinus</span>
        - 2 years = 55 years
      </span>
      <span class="example">
        <span class="pope-info"><span class="antipope-tag">Antipope #12</span> Boniface VII</span>
        , who served 2 terms and had several opponents gets arithmetic mean of
        <span class="pope-info"><span class="pope-tag">Pope #134</span> Benedict VI</span>,
        <span class="pope-info"><span class="pope-tag">Pope #135</span> Benedict VII</span> and
        <span class="pope-info"><span class="pope-tag">Pope #136</span> John XIV</span> - 2 years.
      </span>
    </p>

    <h5>Rules for Hidden Popes</h5>
    <p>
      Rule for Hidden popes Age: In case of simultaneous Pope + Antipope + Hidden pope situation + 2 years to average of pope+antipope (as hidden papacy is probably less dangerous) E.g.:

      <span class="example">
        <span class="pope-info"><span class="antipope-tag">Antipope #36</span> Benedict XIV</span>
        (Bernard Garnier) gets
        <span class="pope-info"><span class="pope-tag">Pope #206</span> Martin V</span> +
        <span class="pope-info"><span class="antipope-tag">Antipope #35</span> Clement VII</span> / 2 + 2 = 71,5 years
      </span>
    </p>
  </div>

  <h4><a id="who"></a>Who is behind the game?</h4>
  <p>
    The game was created by Laura and Matias.
  </p>
  <p>
    Should you have any questions use the <a href="/feedback" on:click|preventDefault={() => navigate('/feedback')}>feedback form</a>.
  </p>

  <h4><a id="other-credits"></a>Other Credits</h4>
  <p>
    Site template is <a target="_blank" href="https://htmltemplated.com/epilogue.html" rel="noopener noreferrer">Epilogue</a> from
    <a target="_blank" href="https://htmltemplated.com/" rel="noopener noreferrer">HTMLTEMPLATED</a>.
  </p>
  <p>
    Pope images are old public domain art and acquired through Wikipedia. Pope biography extracts are also from Wikipedia.
    "No image" is <a href="https://www.flaticon.com/free-icons/pope" target="_blank" rel="noopener noreferrer">pope icon created by Freepik from Flaticon</a>.
  </p>
  <p>
    Typefaces are
      <a href="https://fonts.google.com/specimen/Dosis" target="_blank" rel="noopener noreferrer">Dosis</a>,
      <a href="https://fonts.google.com/specimen/PT+Serif" target="_blank" rel="noopener noreferrer">PT Serif</a> and
      <a href="https://fonts.google.com/specimen/Source+Sans+3" target="_blank" rel="noopener noreferrer">Source Sans 3</a>,
    from <a href="https://fonts.google.com/" target="_blank" rel="noopener noreferrer">Google Fonts</a>.
  </p>
  <p>
    Other images from <a target="_blank" href="https://unsplash.com" rel="noopener noreferrer">Unsplash</a> (<a target="_blank" href="https://unsplash.com/collections/10518669/outlive/d46c01f45871bd052b35eb25fffa4da3" rel="noopener noreferrer">View individual image credits</a>)
  </p>
  <p>
    Other icons are <a target="_blank" href="https://icons.getbootstrap.com/" rel="noopener noreferrer">Bootstrap Icons</a>.
  </p>
</section>

<style>
  #main {
    padding: 4em 4em 2em 4em;
    background-color: #ffffff;
    border-radius: 4px;
  }

  #main > header:first-child {
    text-align: center;
  }

  h4 {
    margin-top: 2em;
    border-bottom: 1px solid #444444;
  }

  h5 {
    margin-bottom: 0.5em;
  }

  .nested {
    border-left: 4px solid #cccccc;
    padding-left: 1em;
  }

  .example {
    display: block;
    margin: 0.5em 1em;
    background: #f0f0f0;
    padding: 0.5em 1em;
    border-radius: 12px;
  }

  .pope-info {
    font-weight: bold;
    white-space: nowrap;
  }

  .pope-tag,
  .antipope-tag {
    color: #ffffff;
    font-weight: bold;
    border-radius: 0.4em;
    display: inline-block;
    text-transform: uppercase;
    font-size: 0.8em;
    padding: 0.1em 0.7em;
    box-shadow: #00000088 1px 1px 1px;
  }

  .pope-tag {
    background-color: #00aa00;
  }

  .antipope-tag {
    background-color: #aa0000;
  }

pre {
    background: #f8f6f0;
    border-left: 3px solid #ccbbaa;
    padding: 1em 1.2em;
    border-radius: 4px;
    overflow-x: auto;
    font-size: 0.9em;
    margin-bottom: 1em;
  }

  code {
    font-family: 'Courier New', Courier, monospace;
    font-size: .8em;
  }

  code :global(.kw) { color: #d73a49; }
  code :global(.ty) { color: #005cc5; }
  code :global(.fn) { color: #6f42c1; }
  code :global(.nm) { color: #e36209; }

  .eldenring {
    font-family: "PT Serif";
    color: #ddb603;
    white-space: nowrap;
  }

  @media screen and (max-width: 736px) {
    #main {
      padding: 2em 2em 0.1em 2em;
    }
  }

  @media screen and (max-width: 480px) {
    #main {
      padding: 2em 1em 0.1em 1em;
    }
  }
</style>
